import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ShieldCheck, Loader2, ShieldAlert, Check, X } from 'lucide-react';
import PageHeader from '../../components/PageHeader';
import Button from '../../components/ui/Button';
import { Card, CardContent } from '../../components/ui/Card';
import { PbrMark, PbrNotice } from '../../components/cultivar';
import cultivarService from '../../services/cultivar';
import {
  CULTIVAR_APPROVER_ROLE_LABELS,
  type CultivarOrderApproval,
} from '../../types/cultivar';
import { useModuleLicense } from '../../hooks/useModuleLicense';

function fmtMoney(v: string | number | null | undefined, currency: string | undefined): string {
  if (v === null || v === undefined || v === '') return '—';
  const n = typeof v === 'string' ? parseFloat(v) : v;
  if (!Number.isFinite(n)) return '—';
  return `${currency || 'AUD'} ${n.toFixed(2)}`;
}

function ApprovalQueue() {
  const { hasAccess: canRead, loading: roleLoading } = useModuleLicense('cultivar', ['viewer', 'pbr', 'admin']);
  const { hasAccess: canAct } = useModuleLicense('cultivar', ['pbr', 'admin']);

  const [items, setItems] = useState<CultivarOrderApproval[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);
  const [acting, setActing] = useState<number | null>(null);
  const [notesById, setNotesById] = useState<Record<number, string>>({});

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const resp = await cultivarService.listPendingApprovals();
      if (resp.success && resp.data) setItems(resp.data as CultivarOrderApproval[]);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load approvals');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const decide = async (id: number, action: 'approve' | 'reject') => {
    setActing(id);
    setError(null);
    setInfo(null);
    try {
      const notes = notesById[id] || '';
      const resp = action === 'approve'
        ? await cultivarService.approveOrder(id, notes)
        : await cultivarService.rejectOrder(id, notes);
      if (!resp.success) throw new Error(resp.error?.message || `${action} failed`);
      setInfo(action === 'approve' ? 'Approved.' : 'Rejected — order will be cancelled.');
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : `${action} failed`);
    } finally {
      setActing(null);
    }
  };

  if (roleLoading || loading) {
    return <div className="flex items-center justify-center min-h-[40vh]"><Loader2 className="w-6 h-6 animate-spin text-gray-400" /></div>;
  }
  if (!canRead) {
    return (
      <div className="max-w-2xl mx-auto p-6">
        <Card><CardContent className="p-6 flex items-start gap-3">
          <ShieldAlert className="w-5 h-5 text-amber-600 mt-0.5" />
          <div><h3 className="font-semibold">Cultivar seat required</h3></div>
        </CardContent></Card>
      </div>
    );
  }

  return (
    <>
      <PageHeader
        title="Order approvals"
        subtitle="Cultivar orders waiting on your organisation as a marketing or distribution approver."
        icon={<ShieldCheck className="h-6 w-6 text-white" />}
      />
      <div className="max-w-6xl mx-auto p-6 space-y-4">
        {error && <div className="bg-red-50 border border-red-200 text-red-800 rounded px-4 py-2 text-sm">{error}</div>}
        {info && <div className="bg-emerald-50 border border-emerald-200 text-emerald-800 rounded px-4 py-2 text-sm">{info}</div>}

        <PbrNotice />

        <Card>
          <CardContent className="p-0">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-left text-xs uppercase tracking-wide text-gray-500">
                <tr>
                  <th className="px-4 py-2">Order</th>
                  <th className="px-4 py-2">Cultivar</th>
                  <th className="px-4 py-2">Nursery</th>
                  <th className="px-4 py-2">Customer</th>
                  <th className="px-4 py-2 text-right">Qty</th>
                  <th className="px-4 py-2 text-right">Total</th>
                  <th className="px-4 py-2">As</th>
                  <th className="px-4 py-2">Decision</th>
                </tr>
              </thead>
              <tbody>
                {items.length === 0 && (
                  <tr><td colSpan={8} className="px-4 py-6 text-center text-gray-500">No pending approvals.</td></tr>
                )}
                {items.map((a) => (
                  <tr key={a.id} className="border-t align-top">
                    <td className="px-4 py-3">
                      <Link className="text-blue-700 hover:underline" to={`/cultivar/orders/${a.order_id}`}>#{a.order_id}</Link>
                    </td>
                    <td className="px-4 py-3">
                      {a.cultivar_name || '—'}
                      <PbrMark status={a.cultivar_protection_status ?? null} />
                    </td>
                    <td className="px-4 py-3">{a.nursery_org_name || '—'}</td>
                    <td className="px-4 py-3">
                      {a.customer_company || '—'}
                      {a.customer_contact && <div className="text-xs text-gray-500">{a.customer_contact}</div>}
                    </td>
                    <td className="px-4 py-3 text-right">{a.qty_trees}</td>
                    <td className="px-4 py-3 text-right">{fmtMoney(a.frozen_total ?? null, a.currency)}</td>
                    <td className="px-4 py-3">
                      {CULTIVAR_APPROVER_ROLE_LABELS[a.approver_role]}
                      {!a.required && <div className="text-xs text-gray-500">optional</div>}
                    </td>
                    <td className="px-4 py-3">
                      {canAct ? (
                        <div className="space-y-1">
                          <input value={notesById[a.id] || ''} placeholder="Notes"
                            onChange={(e) => setNotesById({ ...notesById, [a.id]: e.target.value })}
                            className="w-full border rounded px-2 py-1 text-xs" />
                          <div className="flex gap-1">
                            <Button size="sm" onClick={() => decide(a.id, 'approve')} disabled={acting !== null}>
                              {acting === a.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />}
                            </Button>
                            <Button variant="danger" size="sm" onClick={() => decide(a.id, 'reject')} disabled={acting !== null}>
                              <X className="w-3.5 h-3.5" />
                            </Button>
                          </div>
                        </div>
                      ) : (
                        <span className="text-xs text-gray-500">A PBR or admin seat is required to approve.</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </CardContent>
        </Card>
      </div>
    </>
  );
}

export default ApprovalQueue;
